import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Team from './components/Team';
import Contact from './components/Contact';
import Footer from './components/Footer';
import About from './components/About';
import Highlights from './components/Highlights';
import Branch from './components/Branches';
import Careers from './Pages/Careers';
import ScrollToTopButton from './components/ScrollToTopButton';
import { useLanguage } from './context/LanguageContext';


function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant' 
    });
  }, [pathname]);
  return null;
}

function App() {
  const { isArabic } = useLanguage();

  const content = {
    en: {
      heroTitlePart1: "Elevating Everyday",
      heroTitleHighlight: "Living",
      heroTitlePart2: "Across the Kingdom.",
      heroDesc: "Providing premium retail and dining experiences across Saudi Arabia, delivering quality, freshness, and exceptional service in every location.",
      btnContact: "Contact Us",
      btnBranches: "Explore Branches",
      vision: "Vision 2030"
    },
    ar: {
      heroTitlePart1: "نرتقي بجودة",
      heroTitleHighlight: "الحياة",
      heroTitlePart2: "في جميع أنحاء المملكة.",
      heroDesc: "تقديم تجارب تجزئة ومطاعم فاخرة في جميع أنحاء المملكة، مع ضمان الجودة والطزاجة والخدمة الاستثنائية في كل موقع.",
      btnContact: "تواصل معنا",
      btnBranches: "اكتشف فروعنا",
      vision: "رؤية ٢٠٣٠"
    }
  };

  const t = isArabic ? content.ar : content.en;

  return (
    <Router>

      <ScrollToTop />
      
      <div className={`min-h-screen font-sans text-slate-900 bg-white selection:bg-saudi-green selection:text-white ${isArabic ? 'rtl' : 'ltr'}`}>
        
        <Header />
        
        <Routes>
          <Route path="/" element={
            <>
              <header className="relative min-h-[95vh] flex items-center pt-24 pb-20 bg-corp-blue overflow-hidden">
                {/* Visual Background Layers */}
                <div className="absolute inset-0 z-0">
                  <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
                  <div className="absolute inset-0 bg-gradient-to-b from-corp-blue via-transparent to-corp-blue/80"></div>
                  
                  <div className={`absolute -top-24 ${isArabic ? '-right-24' : '-left-24'} w-[500px] h-[500px] bg-saudi-green/10 rounded-full blur-[120px]`}></div>
                  <div className={`absolute -bottom-24 ${isArabic ? '-left-24' : '-right-24'} w-[400px] h-[400px] bg-saudi-green/5 rounded-full blur-[100px]`}></div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                  <div className="inline-flex items-center gap-2 mb-8 px-5 py-2 border border-saudi-green/30 rounded-full bg-saudi-green/5 backdrop-blur-md animate-fade-in">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-saudi-green opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-saudi-green"></span>
                    </span>
                    <span className="text-saudi-green text-xs font-black tracking-[0.3em] uppercase">
                      {t.vision}
                    </span>
                  </div>

                  <h1 className="text-5xl md:text-8xl font-black text-white mb-8 tracking-tight leading-[1.1]">
                    {t.heroTitlePart1} <span className="text-saudi-green drop-shadow-[0_0_20px_rgba(34,197,94,0.4)]">{t.heroTitleHighlight}</span> <br className="hidden md:block" /> {t.heroTitlePart2}
                  </h1>
                  
                  <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto mb-12 leading-relaxed font-medium">
                    {t.heroDesc}
                  </p>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                    <a 
                      href="#contact" 
                      className="group relative px-12 py-5 bg-saudi-green text-white rounded-2xl font-bold text-lg overflow-hidden transition-all hover:scale-105 active:scale-95 shadow-xl shadow-saudi-green/20"
                    >
                      <span className="relative z-10">{t.btnContact}</span>
                      <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                    </a>
                    <a 
                      href="#branches" 
                      className="px-12 py-5 text-white border border-white/20 rounded-2xl font-bold text-lg hover:bg-white/10 transition-all backdrop-blur-sm"
                    >
                      {t.btnBranches}
                    </a>
                  </div>
                </div>

                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
                   <span className="text-[10px] text-white uppercase tracking-[0.4em] mb-2">{isArabic ? "اسحب للأسفل" : "Scroll"}</span>
                   <div className="w-[1px] h-16 bg-gradient-to-b from-saudi-green to-transparent"></div>
                </div>
              </header>

              <main>
                <Highlights />
                <About />
                <Branch />
                <Team />
                <Contact />
              </main>
            </>
          } />

          <Route path="/careers" element={<Careers />} />
        </Routes>

        <Footer />

        {/* Floating Scroll to Top Action */}
        <ScrollToTopButton />
        
      </div>
    </Router>
  );
}

export default App;
